const ISSUE_STATUSES = new Set(['CANCELED', 'CANCELLED', 'EXPIRED', 'REJECTED', 'REPLACED'])
const CANCELED_STATUSES = new Set(['CANCELED', 'CANCELLED'])
// Fail closed: only statuses we explicitly accept as live/accepted broker
// states count as verified. Unknown Schwab statuses stay sticky until reviewed.
const VERIFIED_STATUSES = new Set([
  'ACCEPTED',
  'AWAITING_PARENT_ORDER',
  'AWAITING_CONDITION',
  'AWAITING_STOP_CONDITION',
  'FILLED',
  'NEW',
  'PENDING_ACTIVATION',
  'PENDING_ACKNOWLEDGEMENT',
  'QUEUED',
  'WORKING',
])

export function isVerifiedBrokerOrderStatus(status) {
  const normalized = String(status ?? '').trim().toUpperCase()
  return VERIFIED_STATUSES.has(normalized) && !ISSUE_STATUSES.has(normalized)
}

export function normalizeOrderId(value) {
  return String(value ?? '').trim()
}

function normalizeAccount(value) {
  return String(value ?? '').trim()
}

function orderKey(accountNumber, orderId) {
  return `${normalizeAccount(accountNumber)}|${normalizeOrderId(orderId)}`
}

function statusOf(order) {
  return String(order?.status ?? '').trim().toUpperCase()
}

function formatPrice(value) {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? `$${num.toFixed(2)}` : null
}

function brokerOrderSummary(order) {
  if (!order) return ''
  const leg = order.orderLegCollection?.[0] || {}
  const instruction = String(leg.instruction || '').replaceAll('_', ' ')
  const type = String(order.orderType || '').replaceAll('_', ' ')
  const price = formatPrice(order.price)
  const stop = formatPrice(order.stopPrice)
  const quantity = Number(order.quantity ?? order.remainingQuantity)
  const priceText = stop ? ` stop ${stop}` : price ? ` @ ${price}` : ''
  const qtyText = Number.isFinite(quantity) && quantity > 0 ? ` x ${quantity}` : ''
  return [instruction, type].filter(Boolean).join(' ') + priceText + qtyText
}

function roleLabel(row) {
  if (row?.bracketRole === 'TARGET') return 'Target entry'
  if (row?.bracketRole === 'RUNNER') return 'Runner entry'
  if (row?.gatewayBatchAction) return row.gatewayBatchAction
  return row?.orderType || 'Order'
}

function plural(count, word) {
  return `${count} ${word}${count === 1 ? '' : 's'}`
}

function canceledTitle(symbol, count) {
  return `${symbol} ${count === 1 ? 'order' : 'orders'} canceled`
}

export function acceptedOrderResults(results) {
  return (Array.isArray(results) ? results : [])
    .filter(row => row?.success === true && normalizeOrderId(row.orderId))
}

export function buildOrderBatchNotification({ id, symbol, action, results, brokerOrders, error }) {
  const resultRows = Array.isArray(results) ? results : []
  const accepted = acceptedOrderResults(results)
  const immediateFailures = resultRows
    .filter(row => row?.success !== true)
    .map(row => ({
      accountNumber: normalizeAccount(row.account_number ?? row.accountNumber),
      orderId: normalizeOrderId(row.orderId),
      role: roleLabel(row),
      status: 'SUBMIT_FAILED',
      ok: false,
      message: row.error || 'Order was not submitted',
    }))

  const brokerByKey = new Map(
    (Array.isArray(brokerOrders) ? brokerOrders : [])
      .filter(order => normalizeOrderId(order?.orderId))
      .map(order => [orderKey(order.accountNumber ?? order.account_number, order.orderId), order])
  )

  const brokerRows = accepted.map(row => {
    const accountNumber = normalizeAccount(row.account_number ?? row.accountNumber)
    const orderId = normalizeOrderId(row.orderId)
    const brokerOrder = brokerByKey.get(orderKey(accountNumber, orderId))
    const status = statusOf(brokerOrder) || 'NOT_FOUND'
    const ok = Boolean(brokerOrder) && isVerifiedBrokerOrderStatus(status)
    return {
      accountNumber,
      orderId,
      role: roleLabel(row),
      status,
      ok,
      summary: brokerOrderSummary(brokerOrder),
      placedAt: brokerOrder?.enteredTime || brokerOrder?.closeTime || brokerOrder?.entered_time || null,
      duration: brokerOrder?.duration || null,
      message: ok
        ? 'Verified at Schwab'
        : brokerOrder
          ? `Schwab status is ${status}`
          : 'Order id was not found in Schwab order history',
    }
  })

  const details = [...brokerRows, ...immediateFailures]
  const accountNumbers = [
    ...details.map(row => row.accountNumber),
    ...resultRows.map(row => normalizeAccount(row.account_number ?? row.accountNumber)),
  ].filter(Boolean)
  const accountCount = new Set(accountNumbers).size
  const issueAccountCount = new Set(details.filter(row => !row.ok).map(row => row.accountNumber).filter(Boolean)).size
  const hasIssue = Boolean(error) || details.some(row => !row.ok)
  const attemptedCount = resultRows.length || details.length || 1
  const verifiedCount = error ? 0 : details.filter(row => row.ok).length
  const issueCount = Math.max(attemptedCount - verifiedCount, details.filter(row => !row.ok).length)
  const safeSymbol = String(symbol || accepted[0]?.symbol || 'Order').toUpperCase()
  const verifiedMessage = `${verifiedCount}/${attemptedCount} orders verified`
  const reviewCount = issueCount || issueAccountCount || 1
  const reviewMessage = `${plural(reviewCount, 'order')} ${reviewCount === 1 ? 'needs' : 'need'} review`
  const isCancelAction = String(action || '').toUpperCase() === 'CANCEL'
  const allAcceptedOrdersCanceled = isCancelAction
    && Boolean(accepted.length)
    && details.length === accepted.length
    && details.every(row => CANCELED_STATUSES.has(row.status))

  if (!error && allAcceptedOrdersCanceled) {
    return {
      id,
      type: 'order_batch',
      tone: 'warning',
      sticky: false,
      symbol: safeSymbol,
      action,
      title: canceledTitle(safeSymbol, details.length),
      message: `${plural(details.length, 'order')} canceled at Schwab · ${plural(accountCount || 1, 'account')}`,
      attemptedCount,
      verifiedCount: 0,
      issueCount: 0,
      accountCount,
      details,
      createdAt: new Date().toISOString(),
    }
  }

  return {
    id,
    type: 'order_batch',
    tone: hasIssue ? 'error' : 'success',
    sticky: hasIssue,
    symbol: safeSymbol,
    action,
    title: hasIssue
      ? `${safeSymbol} order issue`
      : `${safeSymbol} orders working`,
    message: hasIssue
      ? `${verifiedMessage} · ${reviewMessage}`
      : `${verifiedMessage} · ${plural(accountCount || 1, 'account')}`,
    attemptedCount,
    verifiedCount,
    issueCount,
    accountCount,
    details: error
      ? [{ accountNumber: '', orderId: '', role: 'Reconciliation', status: 'ERROR', ok: false, message: error }]
      : details,
    createdAt: new Date().toISOString(),
  }
}
